/**
 * Goose config writer
 *
 * Skill: ~/.agents/skills/sailfish-autofix/ (cross-client)
 * MCP: ~/.config/goose/config.yaml (YAML append)
 */

import { homedir } from "os";
import { join } from "path";
import { existsSync, readFileSync, writeFileSync, mkdirSync } from "fs";
import type { AgentConfigWriter } from "./base.js";
import { installCrossClientSkill, removeCrossClientSkill, buildMcpEntry } from "./base.js";
import { getCredentialsPath } from "../auth/credentials.js";

const GOOSE_CONFIG = join(homedir(), ".config", "goose", "config.yaml");

const YAML_BEGIN_MARKER = "# BEGIN SAILFISH AUTOFIX MANAGED";
const YAML_END_MARKER = "# END SAILFISH AUTOFIX MANAGED";

/**
 * Append MCP server config to Goose YAML config.
 * Uses markers for clean removal. Merges under existing `extensions:` if present.
 */
function addGooseMcpSection(content: string): string {
  content = removeGooseMcpSection(content);
  const entry = buildMcpEntry();

  const extensionEntry = [
    `  ${YAML_BEGIN_MARKER}`,
    `  sailfish-autofix:`,
    `    type: stdio`,
    `    cmd: ${entry.command}`,
    `    args: ${JSON.stringify(entry.args)}`,
    `    env:`,
    `      SAILFISH_JWT_FILE: "${getCredentialsPath()}"`,
    `  ${YAML_END_MARKER}`,
  ].join("\n");

  // Check if extensions: already exists as a top-level key
  const extensionsMatch = content.match(/^extensions:\s*$/m);
  if (extensionsMatch) {
    // Insert our entry right after the existing extensions: line
    const insertIdx = (extensionsMatch.index ?? 0) + extensionsMatch[0].length;
    return content.slice(0, insertIdx) + "\n" + extensionEntry + content.slice(insertIdx);
  }

  // No extensions: key — add the whole block
  const fullBlock = [
    YAML_BEGIN_MARKER,
    `extensions:`,
    extensionEntry.split("\n").map(l => l.startsWith("  ") ? l : "  " + l).join("\n"),
    YAML_END_MARKER,
  ].join("\n");

  return content.trimEnd() + "\n\n" + fullBlock + "\n";
}

function removeGooseMcpSection(content: string): string {
  // First try: markers nested under extensions:
  const indentedBegin = content.indexOf(`  ${YAML_BEGIN_MARKER}`);
  if (indentedBegin !== -1) {
    const indentedEnd = content.indexOf(`  ${YAML_END_MARKER}`);
    if (indentedEnd !== -1) {
      const before = content.slice(0, indentedBegin).replace(/\n+$/, "");
      const after = content.slice(indentedEnd + `  ${YAML_END_MARKER}`.length).replace(/^\n+/, "");
      return before + (after ? "\n" + after : "\n");
    }
  }

  // Second try: top-level markers (standalone extensions: block we created)
  const beginIdx = content.indexOf(YAML_BEGIN_MARKER);
  if (beginIdx === -1) return content;
  const endIdx = content.indexOf(YAML_END_MARKER);
  if (endIdx === -1) return content;

  const before = content.slice(0, beginIdx).replace(/\n+$/, "");
  const after = content.slice(endIdx + YAML_END_MARKER.length).replace(/^\n+/, "");

  if (!before && !after) return "";
  if (!before) return after;
  if (!after) return before + "\n";
  return before + "\n\n" + after;
}

export const goose: AgentConfigWriter = {
  id: "goose",
  name: "Goose",

  installSkill(global: boolean) {
    installCrossClientSkill(global);
  },

  installMcp() {
    let content = "";
    if (existsSync(GOOSE_CONFIG)) {
      content = readFileSync(GOOSE_CONFIG, "utf-8");
    }
    content = addGooseMcpSection(content);
    mkdirSync(join(homedir(), ".config", "goose"), { recursive: true });
    writeFileSync(GOOSE_CONFIG, content);
  },

  uninstallSkill() {
    removeCrossClientSkill(true);
  },

  uninstallMcp() {
    if (!existsSync(GOOSE_CONFIG)) return;
    let content = readFileSync(GOOSE_CONFIG, "utf-8");
    content = removeGooseMcpSection(content);
    writeFileSync(GOOSE_CONFIG, content);
  },
};
